import React, { useState, useMemo, useRef, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import './Pages/PagesCss/Home.css';

const Home = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const searchContainerRef = useRef(null);
  const searchInputRef = useRef(null);

  const featuredCalculators = [
    {
      category: "BullCalc",
      title: "Trading Calculators",
      description: "Calculate profits, ROI, and trading performance with precision",
      icon: "fa-solid fa-chart-bar",
      calculators: [
        { name: "Buy/Sell Profit Calculator", path: "/StockProfitCalculator", keywords: ["buy", "sell", "profit", "trading"] },
        { name: "Short/Long Position Calculator", path: "/ShortLongCalculator", keywords: ["short", "long", "position"] },
        { name: "Dividend Profit Calculator", path: "/DividendProfitCalculator", keywords: ["dividend", "profit"] },
        { name: "Average Price Calculator", path: "/AveragePriceCalculator", keywords: ["average", "price"] },
        { name: "Stock Breakeven Calculator", path: "/StockBreakevenCalculator", keywords: ["breakeven", "stock"] },
        { name: "Target Price Calculator", path: "/TargetPriceCalculator", keywords: ["target", "price"] },
        { name: "Quantity Estimator", path: "/QuantityEstimatorCalculator", keywords: ["quantity", "estimator"] },
        { name: "Max Buy Calculator", path: "/MaxBuyCalculator", keywords: ["max", "buy"] },
        { name: "Lot Size Finder", path: "/LotSizeFinderCalculator", keywords: ["lot", "size", "finder"] },
      ],
      color: "#19692C"
    },
    { 
      category: "Investo",
      title: "Investment Tools",
      description: "Plan your investments and track portfolio growth",
      icon: "fa-solid fa-briefcase",
      calculators: [
        { name: "SIP / Mutual Fund Calculator", path: "/SIPCalculator", keywords: ["sip", "mutual", "fund"] },
        { name: "Stock Portfolio Calculator", path: "/StockPortfolioCalculator", keywords: ["portfolio", "growth", "stock"] },
        { name: "Retirement Investment Calculator", path: "/RetirementInvestmentCalculator", keywords: ["retirement", "investment"] },
        { name: "Lump Sum Returns Calculator", path: "/LumpSumReturnsCalculator", keywords: ["lump", "sum", "returns"] },
        { name: "Inflation Impact Calculator", path: "/InflationImpactCalculator", keywords: ["inflation", "impact"] },
        { name: "Goal Planner Calculator", path: "/GoalPlannerCalculator", keywords: ["goal", "planner"] },
        { name: "Wealth Projection Calculator", path: "/WealthProjectionCalculator", keywords: ["wealth", "projection"] },
        { name: "Retirement Gap Finder", path: "/RetirementGapFinderCalculator", keywords: ["retirement", "gap", "finder"] },
        { name: "Compounding Visualizer", path: "/CompoundingVisualizerCalculator", keywords: ["compounding", "visualizer"] },
      ],
      color: "#28A745"
    },
    {
      category: "StopLoss",
      title: "Risk Management",
      description: "Manage risks and optimize your trading strategies",
      icon: "fa-solid fa-shield-alt",
      calculators: [
        { name: "Stop Loss Calculator", path: "/StopLossCalculator", keywords: ["margin", "calculator"] },
        { name: "Risk/Reward Calculator", path: "/RiskRewardCalculator", keywords: ["risk", "reward", "ratio"] },
        { name: "Stop Loss Target Calculator", path: "/StopLossTargetCalculator", keywords: ["stop", "loss", "target"] },
        { name: "Position Size Calculator", path: "/PositionSizeCalculator", keywords: ["position", "size"] },
        { name: "Stock Volatility Risk Calculator", path: "/StockVolatilityRiskCalculator", keywords: ["volatility", "risk", "stock"] },
        { name: "Max Loss Calculator", path: "/MaxLossCalculator", keywords: ["max", "loss"] },
        { name: "Trade Risk Analyzer", path: "/TradeRiskAnalyzer", keywords: ["trade", "risk", "analyzer"] },
        { name: "Risk Per Trade Finder", path: "/RiskPerTradeFinder", keywords: ["risk", "trade", "finder"] },
        { name: "Risk Exposure Meter Calculator", path: "/RiskExposureMeterCalculator", keywords: ["risk", "exposure", "meter"] },
      ],
      color: "#21963A"
    },
    {
      category: "Taxify",
      title: "Tax Calculators",
      description: "Calculate taxes and optimize your financial decisions",
      icon: "fa-solid fa-file-invoice-dollar",
      calculators: [
        { name: "Capital Gains Tax Calculator", path: "/StockCapitalGainsCalculator", keywords: ["capital", "gains", "tax"] },
        { name: "Brokerage Fee Calculator", path: "/BrokerageCalculator", keywords: ["transaction", "fee", "brokerage"] },
        { name: "Dividend Tax Calculator", path: "/DividendTaxCalculator", keywords: ["dividend", "tax"] },
        { name: "Tax Estimator", path: "/TaxEstimatorCalculator", keywords: ["tax", "estimator"] },
        { name: "Net Profit After Tax Calculator", path: "/NetProfitAfterTaxCalculator", keywords: ["net", "profit", "tax"] },
        { name: "Post-Tax Return Calculator", path: "/PostTaxReturnCalculator", keywords: ["post", "tax", "return"] },
        { name: "Tax Bracket Finder", path: "/TaxBracketFinder", keywords: ["tax", "bracket", "finder"] },
        { name: "Effective Tax Rate Calculator", path: "/EffectiveTaxRateCalculator", keywords: ["effective", "tax", "rate"] },
        { name: "Tax Deductions Calculator", path: "/TaxDeductionsCalculator", keywords: ["tax", "deductions"] },
      ],
      color: "#16A34A"
    }
  ];

  const stats = [
    { number: "50K+", label: "Calculations Made" },
    { number: "95%", label: "Accuracy Rate" },
    { number: "100%", label: "Free Tools" }
  ];

  const allCalculators = useMemo(() => {
    return featuredCalculators.flatMap(category => 
      category.calculators.map(calc => ({
        ...calc,
        category: category.title,
        categoryColor: category.color,
        categoryIcon: category.icon
      }))
    );
  }, []);

  const filteredCalculators = useMemo(() => {
    if (!searchQuery.trim()) return [];
    
    const query = searchQuery.toLowerCase();
    return allCalculators.filter(calc => 
      calc.name.toLowerCase().includes(query) ||
      calc.keywords.some(keyword => keyword.toLowerCase().includes(query)) ||
      calc.category.toLowerCase().includes(query)
    );
  }, [searchQuery, allCalculators]);

  const handleCategoryToggle = (categoryTitle) => {
    setExpandedCategory(expandedCategory === categoryTitle ? null : categoryTitle);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (filteredCalculators.length > 0) {
      window.location.href = filteredCalculators[0].path;
    }
  };

  const handleCalculatorClick = () => {
    setSearchQuery('');
    setIsSearchFocused(false);
  };

  // Handle click outside to close search results
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setIsSearchFocused(false);
      }
    };

    if (isSearchFocused) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSearchFocused]);

  const handleSearchFocus = () => {
    setIsSearchFocused(true);
  };

  const handleSearchBlur = () => {
    // Use a timeout to allow clicks on search results to register
    setTimeout(() => {
      if (searchContainerRef.current && 
          !searchContainerRef.current.contains(document.activeElement)) {
        setIsSearchFocused(false);
      }
    }, 200);
  };

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-container">
          <div className="hero-content">
            <h1 className="hero-title">
              Professional
              <span className="gradient-text"> Financial Calculators</span>
            </h1>
            <p className="hero-description">
              Advanced financial tools for traders, investors, and professionals. 
              Make informed decisions with our comprehensive suite of calculators 
              designed for accuracy and ease of use.
            </p>

            {/* Search Bar */}
            <div className="search-container" ref={searchContainerRef}>
              <form onSubmit={handleSearchSubmit} className="search-form">
                <div className="search-input-wrapper">
                  <i className="fas fa-search search-icon"></i>
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search for calculators (e.g., profit calculator, tax, retirement...)"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={handleSearchFocus}
                    onBlur={handleSearchBlur}
                    className="search-input"
                    autoComplete="off"
                  />
                  {searchQuery && (
                    <button 
                      type="button" 
                      className="clear-search"
                      onClick={() => {
                        setSearchQuery('');
                        searchInputRef.current?.focus();
                      }}
                      onMouseDown={(e) => e.preventDefault()} // Prevent blur on button click
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                  <button 
                    type="submit" 
                    className="search-button"
                    onMouseDown={(e) => e.preventDefault()} // Prevent blur on button click
                  >
                    Search
                  </button>
                </div>
              </form>

              {/* Search Results - Now visible based on searchQuery and isSearchFocused */}
              {searchQuery.trim() && isSearchFocused && (
                <div className="search-results">
                  <div className="results-header">
                    <div className="results-title">
                      <i className="fas fa-search"></i>
                      <h4>Search Results</h4>
                    </div>
                    <span className="results-count">{filteredCalculators.length} calculators found</span>
                  </div>
                  
                  {filteredCalculators.length > 0 ? (
                    <div className="results-list">
                      {filteredCalculators.map((calc, index) => (
                        <NavLink
                          key={index}
                          to={calc.path}
                          className="result-item"
                          onClick={handleCalculatorClick}
                          onMouseDown={(e) => e.preventDefault()} // Keep dropdown open until click completes
                        >
                          <div 
                            className="result-icon"
                            style={{background: calc.categoryColor}}
                          >
                            <i className={calc.categoryIcon}></i>
                          </div>
                          <div className="result-info">
                            <div className="result-name">{calc.name}</div>
                            <div className="result-keywords">
                              {calc.keywords.slice(0, 3).join(' • ')}
                            </div>
                          </div>
                          <div className="result-category">{calc.category}</div>
                          <i className="fas fa-arrow-right result-arrow"></i>
                        </NavLink>
                      ))}
                    </div>
                  ) : (
                    <div className="no-results">
                      <i className="fas fa-search"></i>
                      <p>No calculators found for "{searchQuery}"</p>
                      <small>Try different keywords</small>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="hero-buttons">
              <a href="/StockCapitalGainsCalculator" rel="noopener noreferrer" className="btn btn-primary">
                Start Calculating
              </a>
              <a href="/About" rel="noopener noreferrer" className="btn btn-secondary1 link-green">
                Learn More
              </a>
            </div>
            <div className="container">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div key={index} className="stat-card">
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
          </div>
          <div className="hero-visual">
            <div className="calculator-preview">
              <div className="preview-header">
                <div className="preview-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
              <div className="preview-content">
                <div className="preview-line"></div>
                <div className="preview-line short"></div>
                <div className="preview-buttons">
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Calculators */}
      <section className="featured">
        <div className="container">
          <div className="section-header">
            <h2>Featured Calculators</h2>
            <p>Comprehensive financial tools for every need</p>
          </div>
          <div className="calculators-grid">
            {featuredCalculators.map((category, index) => (
              <div key={index} className="calculator-category">
                <div 
                  className="category-header"
                  onClick={() => handleCategoryToggle(category.title)}
                >
                  <div className="category-icon">
                    <i className={category.icon}></i>
                  </div>
                  <div>
                    <span className="category-tag" style={{ backgroundColor: category.color }}>
                      {category.category}
                    </span>
                    <h3 className="category-title">{category.title}</h3>
                  </div>
                  <button 
                    className="toggle-button" 
                    aria-label={`Toggle ${category.title} calculators`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCategoryToggle(category.title);
                    }}
                  >
                    <i className={`fas fa-chevron-${expandedCategory === category.title ? 'up' : 'down'}`}></i>
                  </button>
                </div>
                <p className="category-description">{category.description}</p>
                
                <div className={`calculators-list ${expandedCategory === category.title ? 'expanded' : ''}`}>
                  {category.calculators.map((calc, calcIndex) => (
                    <NavLink
                      key={calcIndex}
                      to={calc.path}
                      className="calculator-link"
                      onClick={handleCalculatorClick}
                    >
                      <span className="link-icon">→</span>
                      {calc.name}
                    </NavLink>
                  ))}
                </div>
                
                <button 
                  className="expand-button"
                  onClick={() => handleCategoryToggle(category.title)}
                >
                  {expandedCategory === category.title ? 'Show Less' : `Show All ${category.calculators.length} Calculators`}
                  <i className={`fas fa-chevron-${expandedCategory === category.title ? 'up' : 'down'}`}></i>
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Optimize Your Finances?</h2>
            <p>Join thousands of users who trust our calculators for their financial decisions</p>
            <div className="cta-buttons">
              <a href="/StockProfitCalculator" rel="noopener noreferrer" className="btn btn-primary btn-large">
                Get Started Free
              </a>
              <a href="/About" rel="noopener noreferrer" className="btn btn-secondary btn-large">
                Learn How It Works
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;